import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PromptBox from "../components/PromptBox";
import SkyBackdrop from "../components/brand/SkyBackdrop";
import HomeTopBar from "../components/home/HomeTopBar";
import ContinueWorking from "../components/home/ContinueWorking";
import TemplateGallery from "../components/home/TemplateGallery";
import RenameDialog from "../components/home/RenameDialog";
import LowBalanceBar from "../components/LowBalanceBar";
import DomainRenewalBar from "../components/DomainRenewalBar";
import NotifyPrompt from "../components/notify/NotifyPrompt";
import AppBanner, { useAppBanner } from "../components/getapp/AppBanner";
import { PENDING_KEY } from "../context/AuthModalContext";
import { api, errMsg } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const DeleteDialog = ({ project, onCancel, onConfirm }) => (
  <AlertDialog open={!!project} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent className="bg-white border-slate-200 rounded-3xl">
      <AlertDialogHeader>
        <AlertDialogTitle className="text-slate-900">Delete “{project?.name}”?</AlertDialogTitle>
        <AlertDialogDescription>This removes the project, its chat, every version and its published site. This can't be undone.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-full border-slate-200 text-slate-700">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} data-testid="delete-confirm-btn" className="rounded-full bg-rose-600 hover:bg-rose-500 text-white">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default function Home() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toRename, setToRename] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [view, setView] = useState("build");
  const appBanner = useAppBanner();
  const pendingDone = useRef(false);

  const [loadError, setLoadError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    for (let i = 0; i < 4; i++) {
      try {
        const list = await api.projects();
        if (seq === loadSeq.current) { setProjects(list); setLoadError(false); setRetrying(false); }
        return;
      } catch { await new Promise((r) => setTimeout(r, 2000)); }
      if (seq !== loadSeq.current) return;
    }
    setLoadError(true);
    setRetrying(false);
    setProjects((p) => p || []);
  }, []);
  const retry = () => { setRetrying(true); load(); };

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);

  const create = useCallback(async (prompt, model, opts = {}) => {
    setBusy(true);
    try {
      const r = await api.createProject(prompt, model, opts);
      navigate(`/project/${r.project.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not start the build"));
      setBusy(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (pending && !pendingDone.current) {
      pendingDone.current = true;
      sessionStorage.removeItem(PENDING_KEY);
      try { const p = JSON.parse(pending); create(p.prompt, p.model, p.opts || { mode: "plan" }); } catch { /* ignore */ }
    }
  }, [load, create]);

  const confirmDelete = async () => {
    try { await api.deleteProject(toDelete.id); toast.success(`Deleted “${toDelete.name}”`); load(); } catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };
  const duplicate = async (p) => {
    try { const np = await api.duplicateProject(p.id); toast.success(`Created “${np.name}”`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (p, name) => {
    try {
      const np = await api.renameProject(p.id, name);
      setProjects((list) => list.map((x) => (x.id === p.id ? { ...x, name: np.name } : x)));
      toast.success(`Renamed to “${np.name}”`);
      setToRename(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pickTemplate = (t) => {
    setPrefill({ text: t.prompt, at: Date.now() });
    setView("build");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(`Loaded the “${t.name}” template — edit it, then build`);
  };

  return (
    <div className="relative min-h-screen bg-[#F5F8FC] overflow-x-hidden" data-testid="home-page">
      <SkyBackdrop />
      {appBanner.show && <AppBanner onClose={appBanner.close} className="relative z-30" />}
      <HomeTopBar view={view} setView={setView} />
      <LowBalanceBar className="mb-4" />
      <DomainRenewalBar className="mb-4" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6"><NotifyPrompt where="home" className="mt-2 mb-6" /></div>
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pb-24">
        {view === "build" ? (
          <>
            <section className="pt-12 sm:pt-20 fade-up">
              <h1 className="text-center text-[28px] sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-8 sm:mb-10" data-testid="home-greeting">
                What will you build today?
              </h1>
              <PromptBox onSubmit={create} loading={busy} testidPrefix="home" prefill={prefill} />
            </section>
            <ContinueWorking projects={projects} onDelete={setToDelete} onDuplicate={duplicate} onRename={setToRename} error={loadError} onRetry={retry} retrying={retrying} />
          </>
        ) : (
          <div className="pt-10 fade-up"><TemplateGallery onUse={pickTemplate} /></div>
        )}
      </main>
      <DeleteDialog project={toDelete} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />
      <RenameDialog project={toRename} onCancel={() => setToRename(null)} onSave={rename} />
    </div>
  );
}
